import { defaultPalette } from '../../../../theme/idsTheme'
import { capitalizeFirstLetter } from '../../../../utils/stringUtils'
import { translateIdentityInformations, translatePatientHistoryLabel } from '../../../INSi/_utils'
import { ActionTranslation } from '../_props'
import { HistoryRow, HistoryRowDetail, IsExpendableCheck, HistoryRowDetailType, RowDetailTranlations } from './_props'

export const actionTranslation: ActionTranslation = {
    UPDATE: {
        action: 'Modification',
        colors: {
            background: defaultPalette.blue.shadow,
            color: defaultPalette.blue.main,
        },
    },
    CREATE: {
        action: 'Création',
        colors: {
            background: defaultPalette.green.ultraShadow,
            color: defaultPalette.green.main,
        },
    },
    DELETE: {
        action: 'Suppression',
        colors: {
            background: defaultPalette.red.shadow,
            color: defaultPalette.red.main,
        },
    },
    ACCESS: {
        action: 'Accès',
        colors: {
            background: defaultPalette.blue.shadow,
            color: defaultPalette.blue.main,
        },
    },
    SYNC: {
        action: 'Appel au téléservice INSi',
        colors: {
            background: defaultPalette.blue.shadow,
            color: defaultPalette.blue.main,
        },
    },
    SERVER: {
        action: 'Vérification INSi automatisée',
        colors: {
            background: defaultPalette.blue.shadow,
            color: defaultPalette.blue.main,
        },
    },
}

export const sortHistoryRows = (historyRows: HistoryRow[], type: 'ASC' | 'DESC' = 'DESC') =>
    historyRows.sort((a, b) => {
        const dateA = a?.date ?? ''
        const dateB = b?.date ?? ''

        if (dateA < dateB) {
            return type === 'ASC' ? -1 : 1
        }
        if (dateA > dateB) {
            return type === 'ASC' ? 1 : -1
        }

        return 0
    })

export const rowDetailTranlations: RowDetailTranlations = {
    label: {
        callType: "type d'appel",
        insNum: 'numéro INS',
        returnType: 'type de retour',
        oid: 'OID',
        identityTraits: "traits d'identité",
        insHistory: 'historique INS',
    },
    value: {
        type_02: '02',
        type_00: '00',
        type_01: '01',
        VITALE_CARD: 'par carte vitale',
        IDENTITY_TRAITS: "par traits d'identité",
        VERIFICATION: 'vérification',
    },
}

export const patientHistoryDetailToRowDetail = <T extends Record<string, string | null | undefined>>(
    param: T | undefined,
    escapedKeys?: string[],
): HistoryRowDetail[] | undefined => {
    if (!param) return undefined

    const computedEscapedKeys = [['__typename'], escapedKeys ?? []].flat()

    const res = Object.entries(param).reduce<HistoryRowDetail[]>((prev, curr) => {
        const [key, value] = curr

        if (!computedEscapedKeys.includes(key) && value !== undefined && value !== null) {
            prev.push({
                label: rowDetailTranlations.label[key] ?? key,
                value: rowDetailTranlations.value[value] ?? value,
            })
        }

        return prev
    }, [])

    return res
}

const isExpendableMap: IsExpendableCheck[] = [
    { type: 'history', checks: ['inshistory', 'historique ins'] },
    { type: 'identityInformation', checks: ['identitytraits', "traits d'identité"] },
]

export const getDetailType = (type: string) =>
    isExpendableMap.find((x) => x.checks.includes(type.toLocaleLowerCase()))?.type

export const isDetailExpendable = (detail: HistoryRowDetail | undefined, type?: HistoryRowDetailType) => {
    if (detail === undefined) return false

    const dataSet = type ? isExpendableMap.find((x) => x.type === type) : isExpendableMap

    if (!dataSet) return false

    return [dataSet].flat().find((item) => item.checks.includes(detail.label.toLocaleLowerCase())) ?? false
}

const getReadableDetailProcesses: Record<HistoryRowDetailType, (detail: HistoryRowDetail) => HistoryRowDetail> = {
    history: (detail) => {
        try {
            const parsedValue = detail?.value ? JSON.parse(detail.value) : detail.value

            // val is never, no types can be provided fo the INSi connector
            const computedValue = parsedValue.map((val: never) => {
                const x = Object.keys(val).reduce<string[]>((prev, curr) => {
                    if (val[curr]) {
                        const label = translatePatientHistoryLabel(curr)

                        prev.push(`${capitalizeFirstLetter(label)} : ${val[curr]}`)
                    }

                    return prev
                }, [])

                return JSON.stringify(x)
            })

            return {
                ...detail,
                value: JSON.stringify(computedValue),
            }
        } catch (error) {
            console.log(error)

            return detail
        }
    },
    identityInformation: (detail) => {
        try {
            const value = detail?.value
                ? translateIdentityInformations(JSON.parse(detail.value)).map(
                      (translated) => `${capitalizeFirstLetter(translated.label)} : ${translated.value}`,
                  )
                : undefined

            return {
                ...detail,
                value: value ? value.join(', ') : undefined,
            }
        } catch (_error) {
            return detail
        }
    },
}

export const getReadableDetail = (detail: HistoryRowDetail[] | undefined) => {
    if (!detail) return undefined

    return detail.map((item) => {
        const type = getDetailType(item.label)

        return type ? getReadableDetailProcesses[type](item) : item
    })
}
